import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import debounce from 'lodash/debounce'

import { ToggleGroup, Text } from '@buffer-mono/popcorn'

import type { BillingInterval } from '../../../../gql/graphql'

import { YEARLY_SAVINGS_MONTHS } from '../../constants'
import { BillingIntervalValues } from '../../types'
import { SavingsHint } from '../SavingsHint'

import styles from './BillingToggle.module.css'

interface BillingToggleProps {
  value: BillingInterval
  onChange: (value: BillingInterval) => void
}

/**
 * A toggle component that allows users to switch between monthly and yearly billing cycles.
 * Shows a savings hint and hover card when monthly billing is selected.
 *
 * @param value - Current selected billing cycle
 * @param onChange - Callback function when billing cycle changes
 */
const BillingToggle = ({
  value,
  onChange,
}: BillingToggleProps): JSX.Element => {
  const saveHintRef = useRef<HTMLDivElement>(null)
  const [hideHoverCard, setHideHoverCard] = useState(false)
  const [showDiscountConfirmation, setShowDiscountConfirmation] =
    useState(false)
  const [hintVisible, setHintVisible] = useState(false)
  const saveHintHidden = value === BillingIntervalValues.year
  const previousBillingValue = useRef(value)

  useEffect(() => {
    const handleResize = debounce(() => {
      if (!saveHintHidden && saveHintRef.current) {
        const isVisible = saveHintRef.current.checkVisibility?.() ?? true
        setHideHoverCard(isVisible)
      }
    }, 100)

    if (!saveHintHidden) handleResize()

    window.addEventListener('resize', handleResize)
    return (): void => {
      window.removeEventListener('resize', handleResize)
    }
  }, [saveHintHidden])

  // Manage hint visibility - show hint when we need to display a message
  useEffect(() => {
    // Show hint for monthly savings message (when on monthly plan)
    const showMonthlySavings = !saveHintHidden && !hideHoverCard

    // Determine if hint should be visible
    const shouldShowHint = showDiscountConfirmation || showMonthlySavings
    setHintVisible(shouldShowHint)
  }, [showDiscountConfirmation, saveHintHidden, hideHoverCard])

  // Track actual value transitions for discount confirmation
  useEffect(() => {
    const wasMonth =
      previousBillingValue.current === BillingIntervalValues.month
    const isYear = value === BillingIntervalValues.year

    // Show confirmation only on actual month → year transition
    if (wasMonth && isYear) {
      setShowDiscountConfirmation(true)
    }

    // Reset confirmation when on monthly
    if (value === BillingIntervalValues.month) {
      setShowDiscountConfirmation(false)
    }

    previousBillingValue.current = value
  }, [value])

  const onChangeHandler = (newValue: string): void => {
    const isValidBillingInterval = (value: string): value is BillingInterval =>
      Object.values<string>(BillingIntervalValues).includes(value)

    if (isValidBillingInterval(newValue)) {
      onChange(newValue)
    }
  }

  return (
    <div className={styles.container}>
      <ToggleGroup
        defaultValue={BillingIntervalValues.year}
        onChange={onChangeHandler}
        value={value}
        size="large"
        className={styles.toggleGroup}
      >
        <ToggleGroup.Item
          value={BillingIntervalValues.month}
          className={styles.toggleItem}
        >
          Pay Monthly
        </ToggleGroup.Item>

        <ToggleGroup.Item
          value={BillingIntervalValues.year}
          className={styles.toggleItem}
        >
          Pay Yearly
          {!saveHintHidden && (
            <Text className={styles.savingsHint} ref={saveHintRef}>
              {' '}
              · Save {YEARLY_SAVINGS_MONTHS} months
            </Text>
          )}
        </ToggleGroup.Item>
      </ToggleGroup>

      <SavingsHint
        visible={hintVisible}
        key={showDiscountConfirmation ? 'discount' : 'savings'}
      >
        {showDiscountConfirmation
          ? '🎉 Yearly discount applied!'
          : `💸 Save ${YEARLY_SAVINGS_MONTHS} months`}
      </SavingsHint>
    </div>
  )
}

export default BillingToggle
